import * as React from 'react';

import type {DripProgressionEditionsFragment} from 'nutshell-graphql-types';

import {appHistory} from '../../../../../../history';

import {colors} from '../../colors/colors';
import {CancelCircleIcon} from '../../icon/svg/cancel-circle-icon';
import {ClockRegularIcon} from '../../icon/svg/clock-regular-icon';
import {DripsRegularIcon} from '../../icon/svg/drips-regular-icon';
import {PencilOutlineIcon} from '../../icon/svg/pencil-outline-icon';
import {StageGoalIcon} from '../../icon/svg/stage-goal-icon';
import {UserOutlineIcon} from '../../icon/svg/user-outline-icon';
import {Stack} from '../../layout/stack/stack';
import {Toolbar} from '../../toolbar/toolbar';
import {Body} from '../../typography/body';
import {useResizeObserver} from '../../utils/hooks/use-resize-observer';

import {AutomatedEmailRow} from '../automated-email-row/automated-email-row';

import {getHumanReadableTimespan} from './helpers';

import styles from './drip-sequence-row.module.css';

type Props = {
    id: string;
    title: string;
    htmlUrl: string;
    messages: DripProgressionEditionsFragment[];
    startTrigger: string | null | undefined;
    stopCondition: string | null | undefined;
    onCancelButtonClick: (dripId: string, buttonRef?: HTMLElement | null | undefined) => void;
    onViewMessage: (emailTemplateId: string) => void;
    forceShowToolbar?: boolean | null | undefined;
    recipientName: string;
};

export function DripSequenceRow(props: Props) {
    const {onCancelButtonClick} = props;

    const [shouldShowToolbar, setShouldShowToolbar] = React.useState<boolean>(false);
    const [lineHeight, setLineHeight] = React.useState<number>(0);

    const emailRowsRef = React.useRef<HTMLElement | null | undefined>(null);
    const cancelButtonRef = React.useRef<HTMLElement | null | undefined>(null);

    const [width] = useResizeObserver(emailRowsRef.current);

    React.useEffect(() => {
        if (emailRowsRef.current) {
            const height = emailRowsRef.current.offsetHeight;
            setLineHeight(height - 12);
        }
    }, [props.messages, width]);

    const toolbarOptions = React.useMemo(() => {
        return [
            {
                id: 'cancel',
                icon: CancelCircleIcon,
                tooltipText: 'Cancel sequence',
                onClick: () => {
                    onCancelButtonClick(props.id, cancelButtonRef.current);
                },
                // @ts-expect-error
                getRef: (ref) => {
                    cancelButtonRef.current = ref;
                },
            },
            {
                id: 'edit',
                icon: PencilOutlineIcon,
                tooltipText: 'Edit sequence',
                onClick: () => {
                    appHistory.push(props.htmlUrl);
                },
            },
        ];
    }, [props.id, onCancelButtonClick, cancelButtonRef, props.htmlUrl]);

    return (
        <div className={styles['drip-sequence-grid']}>
            <div className={styles['drip-icon-container']}>
                {/* @ts-expect-error */}
                <DripsRegularIcon color={colors.greyLt} size={20} />
            </div>
            <div
                onMouseOver={() => setShouldShowToolbar(true)}
                onMouseLeave={() => setShouldShowToolbar(false)}
                className={styles['header-container']}
            >
                <Body isBold={true}>{props.title}</Body>
                <Stack spacing={4}>
                    <Stack spacing={16} isHorizontal={true}>
                        {props.startTrigger && (
                            <Stack spacing={4} isHorizontal={true} alignItems='center'>
                                <ClockRegularIcon
                                    size={16}
                                    // @ts-expect-error
                                    color={colors.grey}
                                    wrapWithDiv={false}
                                />
                                <Body size='small'>{props.startTrigger}</Body>
                            </Stack>
                        )}
                        {props.stopCondition && (
                            <Stack spacing={4} isHorizontal={true} alignItems='center'>
                                {/* @ts-expect-error */}
                                <StageGoalIcon size={14} wrapWithDiv={false} color={colors.grey} />
                                <Body size='small'>{props.stopCondition}</Body>
                            </Stack>
                        )}
                    </Stack>
                    <Stack spacing={4} isHorizontal={true} alignItems='center'>
                        {/* @ts-expect-error */}
                        <UserOutlineIcon size={14} wrapWithDiv={false} color={colors.grey} />
                        <Body size='small'>Sending to {props.recipientName}</Body>
                    </Stack>
                </Stack>
                {(shouldShowToolbar || props.forceShowToolbar) && (
                    <div className={styles['toolbar-container']}>
                        <Toolbar options={toolbarOptions} />
                    </div>
                )}
            </div>
            <div className={styles['vertical-line-container']}>
                <div style={{height: `${lineHeight}px`}} className={styles['left-vertical-line']} />
            </div>
            {/* @ts-expect-error */}
            <div ref={emailRowsRef} className={styles['email-rows-cell']}>
                {props.messages.map((message) => (
                    <AutomatedEmailRow
                        key={message.edition.id}
                        id={
                            message.edition.emailTemplate
                                ? message.edition.emailTemplate.id
                                : message.edition.id
                        }
                        title={message.edition.name}
                        sent={message.sent}
                        scheduledTime={message.edition.scheduledTime}
                        sendTime={null}
                        delay={
                            message.edition.delay
                                ? getHumanReadableTimespan(
                                      message.edition.delay.timespan,
                                      message.edition.delay.unit
                                  )
                                : null
                        }
                        onViewMessage={props.onViewMessage}
                    />
                ))}
            </div>
        </div>
    );
}
